/**
 * WeMove donation form embed loader.
 *
 * Host page usage:
 *   <div data-wm-donation-form data-locale="it" data-config="climate-petition" data-page="..."></div>
 *   <script async src="https://donations.wemove.eu/embed.js"></script>
 *
 * Env switch lives in the <script src> — staging vs prod is decided by which
 * URL the host page picks. The script reads its own src to derive the iframe
 * origin, so there's no env-detection logic here.
 *
 * Optional: data-locale — defaults to 'en' if missing or empty.
 * Optional: data-config — if present and non-empty, sent as ?config= to the
 * donation form's iframe (the upscale page has no configurations).
 * Only an explicit allowlist of data-* attrs (ALLOWED_DATA_KEYS) is forwarded to
 * the iframe URL as snake_case query params — `data-page` plus preview-only UI
 * knobs. Any other data-* attr is dropped at this boundary. In particular,
 * utm_* is NOT a data-* channel: UTMs travel automatically from the *parent
 * page* URL (see ALLOWED_PARENT_QS below), not from per-embed attributes.
 *
 * Donor-context query params on the *parent page* URL are forwarded into the
 * iframe (mailings, akid, last donation, utm). Anything not on the allowlist
 * is dropped — UI overrides like theme/amounts only travel via the named
 * `data-config` slug or via explicit data-* attrs in dev/preview contexts.
 *
 * Post-donation: when the form reports a completed donation the loader
 * scrolls the host page to the top (the iframe collapses to the short
 * thank-you card, and the donor should land on the page's own context, not
 * mid-whitespace). Host pages can replace that behavior by defining
 * `window.WMDonations.onSuccess = function (iframe) { ... }`.
 *
 * Mid-donation: when the form reports a payment starting to confirm, the loader
 * scrolls the embed itself into view so Stripe's 3DS challenge — drawn inside
 * the iframe — isn't centred below the fold. See scrollIframeIntoView.
 *
 * Upscale flow: `data-flow="upscale"` on the same marker opens the upscale page
 * (/<locale>/upscale), which raises an existing monthly donation:
 *   <div data-wm-donation-form data-flow="upscale" data-locale="it" data-page="..."></div>
 * It forwards its own parent-QS allowlist (UPSCALE_PARENT_QS: the signed akid
 * and rid, the mailing's increase hint, utm_*), uses its own iframe titles and
 * a lower waiting floor, and never scrolls the host page: its UPSCALE_SUCCESS
 * message only calls WMDonations.onSuccess, when the host defines one. Any
 * other data-flow value, or none, opens the donation form.
 */
(function () {
  'use strict';

  // Two params are intentionally absent:
  //   `email`   — the form ignores `?email=` (an unsigned param must not prefill
  //               donor PII), so forwarding it would only put the address in the
  //               iframe URL for nothing.
  //   `mailing` — nothing downstream reads it. The mailing id already rides
  //               inside the signed akid (`{mailingId}.{userId}.{hash}`); AK
  //               extracts it server-side from `user.akid`, and donations-api
  //               derives the action source from the same shape
  //               (`akid_has_mailing` in services/actionkit/fields.py). A bare
  //               unsigned `?mailing=` is not trustworthy for attribution and
  //               never reached `parseQueryParams` or the Stripe metadata
  //               allowlist. Dropped 2026-09-18.
  var ALLOWED_PARENT_QS = [
    'akid',
    'ld',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
  ];

  // The upscale page's donor context, forwarded from the host page URL the
  // mailing link opened. `akid` (identity) and `rid` (`{userId}.{sub id}.{hash}`,
  // which subscription) are both signed and verified server-side; `increase` is
  // the amount button the donor clicked in the email, a hint that only picks
  // the layout and the preselected offer. Nothing else on the host URL reaches
  // the upscale iframe — in particular no amount, currency or ld.
  var UPSCALE_PARENT_QS = [
    'akid',
    'rid',
    'increase',
    'utm_source',
    'utm_medium',
    'utm_campaign',
    'utm_content',
  ];

  // Stripe appends these to the parent URL when a redirect-flow payment
  // (PayPal/iDEAL) breaks out of the iframe and the form bounces the top
  // window back here. Forwarding them into the iframe lets it render the
  // success state on the post-redirect mount. `setup_intent` is the iDEAL
  // recurring sign-up, which the iframe turns into a subscription on return.
  var STRIPE_REDIRECT_QS = [
    'redirect_status',
    'payment_intent',
    'payment_intent_client_secret',
    'setup_intent',
  ];

  // Allowlist of dataset keys that get forwarded as iframe QS. `page` is the
  // only one set by real host embeds (campaign/page tracking field). The rest
  // are the /admin/preview tool's dev-override channel, gated on the form side by
  // the `_preview=1` marker. Mirrors ALLOWED_PARENT_QS — an explicit allowlist
  // beats a denylist for URL hygiene. See URL contract in .claude/CLAUDE.md.
  // (`locale`, `config`, `wmDonationForm` are handled separately — not forwarded
  // via this loop.)
  var ALLOWED_DATA_KEYS = [
    'page',
    '_preview',
    'theme',
    'selectedFrequency',
    'recurringInterval',
    'selectedAmount',
    'oneoffAmounts',
    'recurringAmounts',
    'hideFees',
    'feePercentage',
  ];

  // The upscale page reads only `page` from the AK template, plus the
  // /admin/preview marker. It has one design and no configurations, so no
  // theme and no `config`.
  var UPSCALE_DATA_KEYS = ['page', '_preview'];

  // Localized iframe title — assistive tech announces this when focus enters
  // the iframe. Hardcoded English would mispronounce/mislabel for non-EN
  // hosts; bundling a small map here keeps embed.js self-contained.
  var IFRAME_TITLES = {
    en: 'Donation form',
    it: 'Modulo donazione',
    es: 'Formulario de donación',
    fr: 'Formulaire de don',
    de: 'Spendenformular',
    nl: 'Donatieformulier',
    pl: 'Formularz darowizny',
  };

  // Same role for the upscale page; mirrors the `upscale.iframeTitle` label.
  var UPSCALE_IFRAME_TITLES = {
    en: 'Increase your donation',
    it: 'Aumenta la tua donazione',
    es: 'Aumenta tu donación',
    fr: 'Augmentez votre don',
    de: 'Spende erhöhen',
    nl: 'Verhoog je donatie',
    pl: 'Zwiększ swoją darowiznę',
  };

  // Waiting floor before the page's first WM_RESIZE: the upscale card is much
  // shorter than the form, so the form's 480px would leave a visible gap.
  var FORM_MIN_HEIGHT = 480;
  var UPSCALE_MIN_HEIGHT = 320;

  function isUpscale(el) {
    return el.dataset.flow === 'upscale';
  }

  function camelToSnake(s) {
    return s.replace(/[A-Z]/g, function (c) {
      return '_' + c.toLowerCase();
    });
  }

  function resolveFormOrigin() {
    var script = document.currentScript;

    if (!script) {
      var scripts = document.getElementsByTagName('script');

      for (var i = scripts.length - 1; i >= 0; i--) {
        if (scripts[i].src && /\/embed\.js(\?|#|$)/.test(scripts[i].src)) {
          script = scripts[i];
          break;
        }
      }
    }

    if (!script || !script.src) {
      return null;
    }

    try {
      return new URL(script.src).origin;
    } catch (e) {
      return null;
    }
  }

  var FORM_ORIGIN = resolveFormOrigin();

  function buildIframeUrl(el) {
    if (!FORM_ORIGIN) {
      console.error(
        '[wm-donations/embed] Could not resolve form origin from <script src>; skipping render.'
      );
      return null;
    }

    var locale = el.dataset.locale || 'en';

    var params = new URLSearchParams();

    var parentQs = new URLSearchParams(window.location.search);

    // Before `data-config`: the upscale page has no configurations.
    if (isUpscale(el)) {
      return buildUpscaleUrl(el, locale, params, parentQs);
    }

    var config = el.dataset.config;

    if (config) {
      params.set('config', config);
    }

    for (var i = 0; i < ALLOWED_PARENT_QS.length; i++) {
      var key = ALLOWED_PARENT_QS[i];
      var v = parentQs.get(key);

      if (v) {
        params.set(key, v);
      }
    }

    for (var si = 0; si < STRIPE_REDIRECT_QS.length; si++) {
      var sKey = STRIPE_REDIRECT_QS[si];
      var sVal = parentQs.get(sKey);

      if (sVal) {
        params.set(sKey, sVal);
      }
    }

    // === Mailing amount buttons ===
    // The amount buttons of a fundraising mailing carry the ask as
    // `payment_amount` (ActionKit's donate-page name for it: the donor's highest
    // previous contribution plus the button's offset) and optionally
    // `selected_currency`. Rename `payment_amount` → our `selected_amount` and
    // pass the currency through; the form honors both in production via the
    // matching block in query-params.ts (the clicked amount is preselected and
    // heads the one-off chips). A directly-injected parent `selected_amount` is
    // NOT forwarded — only the `payment_amount` name is translated — so the
    // _preview gate on `selected_amount` stays shut for everything but mailings.
    var mailingAmount = parentQs.get('payment_amount');

    if (mailingAmount) {
      params.set('selected_amount', mailingAmount);
    }

    var mailingCurrency = parentQs.get('selected_currency');

    if (mailingCurrency) {
      params.set('selected_currency', mailingCurrency);
    }
    // === END mailing amount buttons ===

    // data-* attrs win over forwarded parent QS — they're explicit per-embed config.
    // Only allowlisted keys are forwarded; unknown data-* attrs are dropped at
    // this boundary so they never reach the iframe URL.
    for (var ai = 0; ai < ALLOWED_DATA_KEYS.length; ai++) {
      var dKey = ALLOWED_DATA_KEYS[ai];
      var dVal = el.dataset[dKey];

      if (dVal == null || dVal === '') {
        continue;
      }
      params.set(camelToSnake(dKey), dVal);
    }

    // Set parent_return_url so redirect-based payments (PayPal/iDEAL) come home.
    params.set('parent_return_url', window.location.href.split('?')[0]);

    return FORM_ORIGIN + '/form/' + encodeURIComponent(locale) + '?' + params.toString();
  }

  // The upscale page has no redirect-flow payments and no mailing amount buttons:
  // only its own allowlists. parent_return_url still travels — it is how the
  // page resolves the host origin for postMessage (the iframe is
  // no-referrer, so document.referrer is empty).
  function buildUpscaleUrl(el, locale, params, parentQs) {
    for (var i = 0; i < UPSCALE_PARENT_QS.length; i++) {
      var key = UPSCALE_PARENT_QS[i];
      var v = parentQs.get(key);

      if (v) {
        params.set(key, v);
      }
    }

    for (var ai = 0; ai < UPSCALE_DATA_KEYS.length; ai++) {
      var dKey = UPSCALE_DATA_KEYS[ai];
      var dVal = el.dataset[dKey];

      if (dVal == null || dVal === '') {
        continue;
      }
      params.set(camelToSnake(dKey), dVal);
    }

    params.set('parent_return_url', window.location.href.split('?')[0]);

    return FORM_ORIGIN + '/' + encodeURIComponent(locale) + '/upscale?' + params.toString();
  }

  function renderInto(el) {
    if (el.hasAttribute('data-wm-rendered')) {
      return;
    }

    var src = buildIframeUrl(el);

    if (!src) {
      return;
    }

    var locale = el.dataset.locale || 'en';
    var upscale = isUpscale(el);
    var titles = upscale ? UPSCALE_IFRAME_TITLES : IFRAME_TITLES;

    var iframe = document.createElement('iframe');
    iframe.src = src;
    // clipboard-write: the form is ours, and without it nothing inside the
    // iframe can copy (Next's dev overlay's copy button, for one).
    iframe.allow = 'payment *; clipboard-write';
    iframe.referrerPolicy = 'no-referrer';
    iframe.title = titles[locale] || titles.en;
    // No fixed height — the form posts WM_RESIZE on body resize and the
    // listener below sets the height. min-height is the floor while waiting
    // for the first message (and the fallback if the form never reaches us).
    //
    // `outline:0` is not cosmetic tidying — it stops a blue focus ring being
    // painted around the whole form in Firefox. Firefox's UA stylesheet
    // deliberately suppresses the iframe focus ring
    // (`iframe:focus-visible{outline-style:none}`), but a host page built with
    // Tailwind's pre-4.3 Preflight ships a bare `:-moz-focusring{outline:auto}`
    // that overrides it, so as soon as the donor clicks into the form the
    // parent's <iframe> takes focus and Firefox draws its platform ring around
    // the entire embed. Chrome and Safari never match `:-moz-focusring`, so it
    // looks fine everywhere else. Tailwind fixed this upstream in Preflight
    // (`:-moz-focusring:where(:not(iframe))`, tailwindlabs/tailwindcss#19795),
    // but we can't wait on every host page to rebuild its CSS — an inline
    // style beats the host's stylesheet, so setting it here fixes the embed on
    // any page. Keep it: it restores the browser default rather than removing
    // a focus indicator, and focus styling *inside* the form is untouched.
    iframe.style.cssText =
      'border:0;outline:0;width:100%;max-width:480px;min-height:' +
      (upscale ? UPSCALE_MIN_HEIGHT : FORM_MIN_HEIGHT) +
      'px;display:block;background:transparent;';

    el.setAttribute('data-wm-rendered', '');
    el.appendChild(iframe);
  }

  // Match the originating iframe by its contentWindow — survives multi-embed
  // pages without needing IDs.
  function findIframeFor(source) {
    var iframes = document.querySelectorAll('[data-wm-donation-form][data-wm-rendered] iframe');

    for (var i = 0; i < iframes.length; i++) {
      if (iframes[i].contentWindow === source) {
        return iframes[i];
      }
    }
    return null;
  }

  // Post-donation, the form swaps to a short thank-you card and the WM_RESIZE
  // below collapses the iframe by roughly a screenful on mobile — leaving the
  // donor parked where the payment section used to be, staring past the
  // shortened embed at host-page whitespace and footer. (Worst with Apple Pay:
  // the wallet button is near the top of the payment section, so the donor
  // never scrolls down to type card details first.) Default response: scroll
  // the host page to the top, so the donor lands on the page's own context
  // with the thank-you below it. Host pages that want something else define
  //
  //   window.WMDonations = window.WMDonations || {};
  //   window.WMDonations.onSuccess = function (iframe) { /* custom */ };
  //
  // (before or after this script loads — the namespace is merged, not
  // replaced) and the default scroll is skipped entirely.
  function prefersReducedMotion() {
    return (
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  function scrollHostToTop() {
    // Standard feature check for smooth scrolling; older engines take the
    // two-arg form and jump instantly, which is also the reduced-motion path.
    if (!prefersReducedMotion() && 'scrollBehavior' in document.documentElement.style) {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } else {
      window.scrollTo(0, 0);
    }
  }

  // Stripe draws its 3DS challenge with position:fixed *inside the iframe*, so
  // the challenge is centred on the iframe's box — not on the host viewport.
  // The embed is routinely several times taller than a phone viewport (a filled
  // form measures ~1100px), so a host page sitting at the top of a tall embed
  // can put the challenge hundreds of pixels below the fold. The donor can't
  // scroll to it from inside either: the form pins html/body to
  // overflow:hidden in there so the iframe never shows its own scrollbar. The
  // result is an invisible challenge and a donation that cannot complete.
  //
  // Centring the iframe in the host viewport puts its middle — where the
  // challenge is — on screen. block:'center' is correct even when the iframe is
  // taller than the viewport: the browser centres it and the middle is what
  // stays visible.
  function scrollIframeIntoView(iframe) {
    if (typeof iframe.scrollIntoView !== 'function') {
      return;
    }

    // The options form is not universally supported; the no-arg call is, and it
    // still brings the element into view (aligned to the nearest edge).
    try {
      iframe.scrollIntoView({
        block: 'center',
        inline: 'nearest',
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    } catch (e) {
      iframe.scrollIntoView();
    }
  }

  // `scrollToTop` is false for the upscale page, which never moves the host.
  function handleDonationSuccess(iframe, scrollToTop) {
    var handler = window.WMDonations && window.WMDonations.onSuccess;

    if (typeof handler === 'function') {
      try {
        handler(iframe);
      } catch (e) {
        // A broken host-page handler must not take the loader down with it.
        console.error('[wm-donations/embed] onSuccess handler threw:', e);
      }
      return;
    }

    if (scrollToTop) {
      scrollHostToTop();
    }
  }

  function handleMessage(event) {
    if (event.origin !== FORM_ORIGIN) {
      return;
    }

    var data = event.data;

    if (!data) {
      return;
    }

    if (data.type === 'WM_PAYMENT_PENDING') {
      var pendingIframe = findIframeFor(event.source);

      if (pendingIframe) {
        scrollIframeIntoView(pendingIframe);
      }
      return;
    }

    // A raised monthly amount ends the upscale flow through the same onSuccess
    // hook as a donation, but with no default scroll: the thank-you card
    // replaces the offers where the donor clicked.
    if (data.type === 'DONATION_SUCCESS' || data.type === 'UPSCALE_SUCCESS') {
      var successIframe = findIframeFor(event.source);

      if (!successIframe) {
        return;
      }

      handleDonationSuccess(successIframe, data.type === 'DONATION_SUCCESS');
      return;
    }

    if (data.type !== 'WM_RESIZE' || typeof data.height !== 'number') {
      return;
    }

    var height = Math.max(0, Math.floor(data.height));

    if (!height) {
      return;
    }

    var iframe = findIframeFor(event.source);

    if (!iframe) {
      return;
    }

    iframe.style.height = height + 'px';
    // The inline min-height (480px; 320px upscale) is only the floor while
    // waiting for the form's first report (and the fallback if it never
    // arrives). Once real
    // measurements flow the content owns the height — without this, the
    // success card (~250px) would sit in a 480px box with dead space below.
    iframe.style.minHeight = '0';
  }

  window.addEventListener('message', handleMessage);

  // After we've handed the redirect params to a freshly-rendered iframe, scrub
  // them from the parent URL so a reload or back-navigation doesn't re-trigger
  // the success state.
  function stripStripeRedirectParams() {
    if (!window.history || !window.history.replaceState) {
      return;
    }
    var qs = new URLSearchParams(window.location.search);
    var changed = false;

    for (var i = 0; i < STRIPE_REDIRECT_QS.length; i++) {
      if (qs.has(STRIPE_REDIRECT_QS[i])) {
        qs.delete(STRIPE_REDIRECT_QS[i]);
        changed = true;
      }
    }

    if (changed) {
      var clean = qs.toString();
      window.history.replaceState(
        {},
        '',
        window.location.pathname + (clean ? '?' + clean : '') + window.location.hash
      );
    }
  }

  function render() {
    var targets = document.querySelectorAll('[data-wm-donation-form]:not([data-wm-rendered])');

    for (var i = 0; i < targets.length; i++) {
      renderInto(targets[i]);
    }

    if (targets.length > 0) {
      stripStripeRedirectParams();
    }
  }

  // Render now for placeholders already parsed, and again on DOMContentLoaded
  // for the rest: with the documented `<script async>` the loader often runs
  // mid-parse, and waiting for the whole document only delays the iframe
  // request. The `data-wm-rendered` marker makes the second pass a no-op for
  // anything the first one handled.
  render();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', render);
  }

  window.WMDonations = window.WMDonations || {};
  window.WMDonations.render = render;
})();
